import { ColumnTable, Icon } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CurrencyOverviewItem } from '../api';

const dictPrefix = 'financialPortal.settings.currencies';

// Única moneda que mindicador.cl publica y que este sistema sincroniza
// automáticamente (WI-16486). Ver useCurrencyColumns.tsx / CurrencyUfSyncService.
const SYNCABLE_CURRENCY_CODE = 'UF';

interface Options {
  showActions: boolean;
  onShowHistory: (currency: CurrencyOverviewItem) => void;
  onSyncRate: (currency: CurrencyOverviewItem) => void;
  syncingId: number | null;
}

export function useCurrencyColumns({
  showActions,
  onShowHistory,
  onSyncRate,
  syncingId,
}: Options): ColumnTable<CurrencyOverviewItem>[] {
  const { t } = useTranslation();

  return useMemo<ColumnTable<CurrencyOverviewItem>[]>(() => {
    const columns: ColumnTable<CurrencyOverviewItem>[] = [
      {
        columnName: 'code',
        headerText: t(`${dictPrefix}.columns.code`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'name',
        headerText: t(`${dictPrefix}.columns.name`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
    ];

    if (!showActions) {
      // Principal currency table only: the secondary table drops the
      // Símbolo column per user request — the code column already
      // identifies the currency there.
      return [
        ...columns,
        {
          columnName: 'symbol',
          headerText: t(`${dictPrefix}.columns.symbol`),
          thClassName: 'text-center',
          tdClassName: 'text-center',
        },
      ];
    }

    return [
      ...columns,
      {
        columnName: 'rate',
        headerText: t(`${dictPrefix}.columns.rate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.rate
            ? row.rate.value.toLocaleString('es-CL')
            : t(`${dictPrefix}.noRate`),
      },
      {
        columnName: 'validFrom',
        headerText: t(`${dictPrefix}.columns.validFrom`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.rate ? dayjs(row.rate.validFrom).format('DD/MM/YYYY') : '-',
      },
      {
        columnName: 'id',
        headerText: '',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: 90,
        // Icon-only action (see courseGroups/parts/ActionCell.tsx for the
        // established pattern): a bare `btn p-0` button with `aria-label` +
        // `title` set to the same copy keeps it accessible and gives the
        // hover tooltip, without adding new i18n keys. The "new rate" action
        // now lives in the section header (see index.tsx), so this column
        // only keeps the history icon.
        cellFormat: ({ row }) => (
          <div className="d-flex justify-content-center" style={{ gap: 8 }}>
            {row.code === SYNCABLE_CURRENCY_CODE ? (
              <button
                type="button"
                className="btn p-0"
                aria-label={t(`${dictPrefix}.columns.sync`)}
                title={t(`${dictPrefix}.columns.sync`)}
                disabled={syncingId === row.id}
                onClick={() => onSyncRate(row)}
              >
                <Icon size="15" name="refresh" color="primary" />
              </button>
            ) : null}
            <button
              type="button"
              className="btn p-0"
              aria-label={t(`${dictPrefix}.columns.history`)}
              title={t(`${dictPrefix}.columns.history`)}
              onClick={() => onShowHistory(row)}
            >
              <Icon size="15" name="classes_history" color="primary" />
            </button>
          </div>
        ),
      },
    ];
  }, [t, showActions, onShowHistory, onSyncRate, syncingId]);
}
